import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
    ClockIcon,
    XCircleIcon,
    Loader2Icon,
    FileTextIcon,
    XIcon,
    ArrowRightIcon,
} from "lucide-react";
import TrainingAuthLayout from "@container/layout/training-auth";
import BreadcrumbNav from "@component/breadcrumb-nav";
import { Button } from "@component/button";
import { Card, CardContent } from "@component/card";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@component/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@component/tabs";
import { RubricBar, rubricGradeBand } from "@component/training/rubric-bar";
import { KnowledgeSourcesSection } from "@component/training/knowledge-sources-section";
import { KnowledgeDocDrawer } from "@component/training/knowledge-doc-drawer";
import { getKbDocument, type KbDocument } from "@service/knowledge-base";
import { SpeechCoachingStrip } from "@component/training/speech-coaching-strip";
import { CoachingMomentCard } from "@component/training/coaching-moment-card";
import { PerformanceInsightsBanner } from "@component/training/performance-insights-banner";
import { RecordingPlayer, type RecordingPlayerHandle } from "@component/training/recording-player";
import HighlightTranscript from "@component/highlight-transcript";
import {
    transcriptToHighlights,
    mapBeSpeechMetrics,
} from "@util/training-feedback-adapter";
import type { SpeechMetrics } from "@util/training-feedback-adapter";
import type { BeSpeechMetrics } from "@type/training";
import { getCallSession, getAiqmAudio } from "@service/aiqm";
import type { AiqmAudioResponse } from "@service/aiqm";
import type {
    CallSession,
    AiqmReport,
    ActionItem,
    TranscriptEntry,
    LatencyPattern,
} from "@type/aiqm";
import {
    GRADE_CONFIG,
    priorityColor,
} from "@type/aiqm";
import type { RubricCriterion, RubricScores, TranscriptTurn, BeFeedbackItem, KnowledgeManifest } from "@type/training";
import {
    RUBRIC_LABELS,
    classifyRubricCriterion,
} from "@type/training";
import type { CoachingDimension } from "@type/training";
import { TrainingErrorCard } from "@component/training/training-async-state";
import { catchErrorLog } from "@util/sentry";
import { cn } from "@util/tailwind";

export default function TrainingAiqmDetailPage() {
    const { id } = useParams<{ id: string }>();
    const [session, setSession] = useState<CallSession | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        if (!id) return;
        let cancelled = false;
        setLoading(true);
        setError(null);
        getCallSession(id)
            .then((s) => {
                if (cancelled) return;
                setSession(s);
                setLoading(false);
            })
            .catch((err: unknown) => {
                if (cancelled) return;
                const e = err instanceof Error ? err : new Error(String(err));
                catchErrorLog(`[aiqm] session detail load failed: ${e.message}`);
                setError(e);
                setLoading(false);
            });
        return () => { cancelled = true; };
    }, [id]);

    return (
        <TrainingAuthLayout>
            <div className="flex flex-col h-[calc(100svh-64px)]">
                <div className="flex-shrink-0 pb-6">
                    <BreadcrumbNav
                        items={[
                            { label: "AI QM", href: "/training/aiqm" },
                            { label: session?.contact_name || session?.filename || "Session Detail" },
                        ]}
                    />
                </div>

                <div className="flex-1 min-h-0">
                {error ? (
                    <TrainingErrorCard
                        title="Could not load session."
                        message="The error has been reported. Try again or refresh."
                        onRetry={() => window.location.reload()}
                    />
                ) : loading ? (
                    <SkeletonDetail />
                ) : !session || !session.id ? (
                    <AiqmEmptyState />
                ) : session.status !== "results_ready" || !session.aiqm_report ? (
                    <PendingState session={session} />
                ) : (
                    <ReportView session={session} report={session.aiqm_report} />
                )}
                </div>
            </div>
        </TrainingAuthLayout>
    );
}

function ReportView({
    session,
    report,
}: {
    session: CallSession;
    report: AiqmReport;
}) {
    const grade = GRADE_CONFIG[report.overall_grade];

    const triggerClass =
        "text-sm bg-transparent border-0 rounded-none pb-3 px-4 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-blue-600 data-[state=active]:text-blue-600 hover:text-blue-600 transition-colors flex-shrink-0 whitespace-nowrap";

    const [audio, setAudio] = useState<AiqmAudioResponse | null>(null);
    const [audioUnavailable, setAudioUnavailable] = useState(false);
    const recordingPlayerRef = useRef<RecordingPlayerHandle | null>(null);
    // Controlled tab + focus signal so clicking a timeline dot can switch to the
    // tab that owns the matching feedback item and auto-expand it. `focus.n` is a
    // monotonic counter so re-clicking the same dot re-opens it after a manual
    // collapse (mirrors the SessionCoachingPanel pattern on the results page).
    const [activeTab, setActiveTab] = useState("overview");
    const [focus, setFocus] = useState<{ ts: string; n: number } | null>(null);
    // Citation chips open the same drawer as the Overview Sources rows.
    const [sourceDoc, setSourceDoc] = useState<KbDocument | null>(null);
    const sourceDocSeq = useRef(0);

    // Guarded against out-of-order responses — see the matching comment in
    // session-coaching-panel.tsx. A drawer that asserts provenance must never
    // show a document other than the one just clicked.
    async function openSourceDoc(documentId: string) {
        const seq = ++sourceDocSeq.current;
        setSourceDoc(null);
        try {
            const doc = await getKbDocument(documentId);
            if (sourceDocSeq.current !== seq) return;
            setSourceDoc(doc);
        } catch {
            if (sourceDocSeq.current !== seq) return;
            setSourceDoc(null);
        }
    }

    // Switching reports invalidates any in-flight fetch.
    useEffect(() => {
        sourceDocSeq.current++;
        setSourceDoc(null);
    }, [session.id]);
    // Chat/TXT sessions have no audio player — a feedback "Go to {ts}" action
    // scrolls the transcript to that message time instead. `transcriptFocus`
    // carries the target time; `.n` bumps so re-clicking the same time re-scrolls.
    const [transcriptFocus, setTranscriptFocus] = useState<{ time: string; n: number } | null>(null);
    // The transcript entry we scrolled to (its normalized clock) — highlighted so
    // the user sees which message the feedback points at.
    const [activeTranscriptTs, setActiveTranscriptTs] = useState<string | null>(null);
    const transcriptScrollRef = useRef<HTMLDivElement | null>(null);

    // Text-chat uploads (*.txt) have no audio — skip the /audio API call entirely
    // and hide the audio section.
    const isTextUpload = (session.filename ?? "").toLowerCase().endsWith(".txt");

    useEffect(() => {
        let cancelled = false;
        setAudio(null);
        setAudioUnavailable(false);
        if (!isTextUpload) {
            getAiqmAudio(session.id)
                .then((data) => {
                    if (cancelled) return;
                    if (data.audio_url) setAudio(data);
                    else setAudioUnavailable(true);
                })
                .catch(() => { if (!cancelled) setAudioUnavailable(true); });
        }
        return () => { cancelled = true; };
    }, [session.id, isTextUpload]);

    const handlePlayFromTimestamp = (timestamp: string) => {
        const ts = (timestamp ?? "").trim();
        // Chat/TXT sessions have no audio — jump the transcript to this message
        // time instead of seeking a player. `.n` bumps so re-clicking re-scrolls.
        if (isTextUpload) {
            if (ts) setTranscriptFocus((prev) => ({ time: ts, n: (prev?.n ?? 0) + 1 }));
        } else {
            recordingPlayerRef.current?.playFrom(timestamp);
        }
        // Switch to the tab that owns the clicked feedback item and signal it to
        // auto-expand (CoachingMomentCard scrolls itself into view in the right
        // panel). Only the rubric tabs carry feedback cards.
        if (!ts || !hasRubric) return;
        const item = (report.feedback_items ?? []).find(
            (i) => (i.timestamp ?? "").trim() === ts,
        );
        if (!item) return;
        const dim = dimensionForItem(item, report.rubric_criteria);
        setActiveTab(dim === "communication" ? "communication" : "issue-resolution");
        setFocus((prev) => ({ ts, n: (prev?.n ?? 0) + 1 }));
    };

    // Memoized so its identity is stable across renders — the transcript-jump
    // effect depends on it (re-runs once rows exist) without firing every render.
    const highlights = useMemo(
        () => transcriptToHighlights(aiqmTranscriptToTurns(session.transcript)),
        [session.transcript],
    );

    const hasRubric = !!report.rubric_scores;
    // llm_results is absent for uploaded sessions with no LLM analysis block.
    const actionItems = report.llm_results?.action_items ?? [];

    // Scroll the transcript to the message a feedback item points at (chat/TXT
    // only). Transcript segments are grouped Q&A pairs, so the feedback time rarely
    // equals a segment time exactly — pick the CLOSEST segment by minutes, then
    // bring it into view within the transcript scroll panel and highlight it.
    useEffect(() => {
        if (!isTextUpload || !transcriptFocus) return;
        const container = transcriptScrollRef.current;
        if (!container) return;
        const targetMin = chatMinutes(transcriptFocus.time);
        if (targetMin === null) return;
        // Pick the CLOSEST segment by minute distance, keyed by its render INDEX
        // so repeated minutes (common in chat logs) resolve to the exact row —
        // not just the first message that shares that minute.
        let bestKey: string | null = null;
        let bestDist = Infinity;
        for (let i = 0; i < highlights.length; i++) {
            const clock = chatClock(highlights[i].time);
            const min = chatMinutes(highlights[i].time);
            if (clock === null || min === null) continue;
            const dist = Math.abs(min - targetMin);
            if (dist < bestDist) {
                bestDist = dist;
                bestKey = `${clock}-${i}`;
            }
        }
        if (!bestKey) return;
        setActiveTranscriptTs(bestKey);
        const el = Array.from(
            container.querySelectorAll<HTMLElement>("[data-transcript-ts]"),
        ).find((n) => n.dataset.transcriptTs === bestKey);
        el?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, [transcriptFocus, isTextUpload, highlights]);

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 h-full lg:overflow-hidden">
            {/* ── Left panel — session info + audio + transcript ──────── */}
            <div className="lg:h-full flex flex-col min-h-0 overflow-hidden">
                {/* Session info card — no outer border */}
                <div className="pb-4 flex-shrink-0">
                    <div className="flex items-start gap-3">
                        <div
                            className={cn(
                                "w-12 h-12 rounded-xl flex items-center justify-center text-lg font-bold flex-shrink-0",
                                grade.bg,
                                grade.color,
                            )}
                        >
                            {report.overall_grade}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-start gap-2">
                                <div className="flex-1 min-w-0">
                                    <h1 className="text-base font-semibold text-gray-900 truncate mb-1">
                                        {session.contact_name || session.filename || "Untitled"}
                                    </h1>
                                    <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                                        <span className="flex items-center gap-1">
                                            <ClockIcon className="w-3.5 h-3.5" />
                                            {report.total_duration_formatted}
                                        </span>
                                        <span>
                                            Analyzed on:{" "}
                                            {new Date(session.created_at).toLocaleString("en-MY", {
                                                day: "numeric",
                                                month: "short",
                                                year: "numeric",
                                                hour: "2-digit",
                                                minute: "2-digit",
                                            })}
                                        </span>
                                        {report.campaign_context?.name && (
                                            <span>{report.campaign_context.name}</span>
                                        )}
                                    </div>
                                </div>
                                <div className="flex items-baseline gap-1 flex-shrink-0 ml-2">
                                    <span className="text-3xl font-bold text-gray-900 tabular-nums leading-none">
                                        {report.overall_score}
                                    </span>
                                    <span className="text-base text-gray-400">/100</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {!isTextUpload && (
                        <div className="mt-3">
                            {audio ? (
                                <RecordingPlayer
                                    ref={recordingPlayerRef}
                                    recordingUrl={audio.audio_url}
                                    feedbackItems={report.feedback_items ?? []}
                                    onTimestampClick={handlePlayFromTimestamp}
                                />
                            ) : audioUnavailable ? (
                                <div className="py-2 text-xs text-gray-500">
                                    Audio is not available for this session.
                                </div>
                            ) : (
                                <div className="py-2 flex items-center gap-2 text-xs text-gray-500">
                                    <Loader2Icon className="w-4 h-4 animate-spin" />
                                    Loading audio…
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Transcript — fills remaining panel height */}
                <div className="mt-10 flex flex-col flex-1 min-h-0 rounded-md overflow-hidden">
                    <div className="p-4 border-b border-gray-200 bg-white flex items-center gap-2 flex-shrink-0">
                        <FileTextIcon className="h-4 w-4 text-blue-600" />
                        <h3 className="font-medium text-gray-800">Transcript</h3>
                        {highlights.length > 0 && (
                            <span className="ml-auto bg-blue-50 text-blue-700 text-xs font-semibold px-2 py-0.5 rounded-full">
                                {highlights.length}
                            </span>
                        )}
                    </div>
                    <div ref={transcriptScrollRef} className="p-4 flex-1 overflow-y-auto">
                        {highlights.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                                <div className="bg-gray-100 p-4 rounded-full mb-4">
                                    <FileTextIcon className="h-8 w-8 text-gray-400" />
                                </div>
                                <p className="text-xs text-gray-500 max-w-xs">
                                    The session transcript will appear here once processing finishes.
                                </p>
                            </div>
                        ) : (
                            highlights.map((segment, index) => {
                                // Unique per-row key (minute + render index) ONLY for
                                // chat/TXT so a feedback "Go to {ts}" click resolves to the
                                // exact row even when several messages share a minute.
                                // Audio rows get no tag and isActive stays false.
                                const rowKey =
                                    isTextUpload && chatClock(segment.time)
                                        ? `${chatClock(segment.time)}-${index}`
                                        : undefined;
                                return (
                                    <div key={index} data-transcript-ts={rowKey}>
                                        <HighlightTranscript
                                            time={segment.time}
                                            question={segment.question}
                                            answer={segment.answer}
                                            interviewerName="CUSTOMER"
                                            candidateName="CUSTOMER AGENT"
                                            isActive={!!rowKey && rowKey === activeTranscriptTs}
                                            onClick={() => handlePlayFromTimestamp(segment.time)}
                                        />
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            </div>

            {/* ── Right panel — insights banner + tabs ─────────────────── */}
            <div className="lg:overflow-y-auto lg:h-full flex flex-col">
                {/* Agent Performance Insights banner */}
                {report.one_line_summary && (
                    <PerformanceInsightsBanner
                        title="Agent Performance Insights"
                        className="mb-4 flex-shrink-0"
                    >
                        {report.one_line_summary}
                    </PerformanceInsightsBanner>
                )}

                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full rounded-none flex-1 flex flex-col min-h-0">
                    <TabsList className="flex w-full bg-transparent h-auto p-0 border-b border-gray-200 justify-start rounded-none overflow-x-auto scrollbar-hide flex-shrink-0">
                        <TabsTrigger value="overview" className={triggerClass}>
                            Overview
                        </TabsTrigger>
                        {hasRubric && (
                            <TabsTrigger value="issue-resolution" className={triggerClass}>
                                Issue Resolution
                            </TabsTrigger>
                        )}
                        {hasRubric && (
                            <TabsTrigger value="communication" className={triggerClass}>
                                Communication
                            </TabsTrigger>
                        )}
                        <TabsTrigger value="delivery" className={triggerClass}>
                            Delivery
                        </TabsTrigger>
                    </TabsList>

                    {/* Overview */}
                    <TabsContent value="overview" className="mt-5 space-y-6 flex-1 overflow-y-auto pb-4 pr-4">
                        {/* Conversation Flow */}
                        <div>
                            <TabSectionHeader title="Conversation Flow" />
                            <p className="text-sm text-gray-700 leading-relaxed">
                                {report.conversation_flow_assessment}
                            </p>

                            {/* Compact rubric score mini-section */}
                            {report.rubric_scores && (
                                <CompactRubricSection
                                    scores={report.rubric_scores}
                                    total={report.rubric_total_score}
                                    criteria={report.rubric_criteria}
                                    isChat={isTextUpload}
                                />
                            )}
                        </div>

                        {/* Action Items */}
                        {actionItems.length > 0 && (
                            <div>
                                <div className="flex items-center justify-between mb-3">
                                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Action Items</p>
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100">
                                        {actionItems.length} identified
                                    </span>
                                </div>
                                <ActionItemsList items={actionItems} />
                            </div>
                        )}

                        {/* Sources — the documents this evaluation was actually
                            given, from the scoring-time manifest. Renders nothing
                            until the BE emits knowledge_manifest. */}
                        <KnowledgeSourcesSection manifest={report.knowledge_manifest} />
                    </TabsContent>

                    {/* Issue Resolution */}
                    {hasRubric && (
                        <TabsContent value="issue-resolution" className="mt-5 flex-1 overflow-y-auto pb-4 pr-4">
                            <AiqmDimensionPanel
                                dim="handling"
                                scores={report.rubric_scores!}
                                criteria={report.rubric_criteria}
                                feedbackItems={report.feedback_items ?? []}
                                knowledgeManifest={report.knowledge_manifest}
                                onOpenSourceDoc={openSourceDoc}
                                onPlayFromTimestamp={handlePlayFromTimestamp}
                                focusTs={focus?.ts ?? null}
                                focusN={focus?.n ?? 0}
                                isChat={isTextUpload}
                            />
                        </TabsContent>
                    )}

                    {/* Communication */}
                    {hasRubric && (
                        <TabsContent value="communication" className="mt-5 flex-1 overflow-y-auto pb-4 pr-4">
                            <AiqmDimensionPanel
                                dim="communication"
                                scores={report.rubric_scores!}
                                criteria={report.rubric_criteria}
                                feedbackItems={report.feedback_items ?? []}
                                knowledgeManifest={report.knowledge_manifest}
                                onOpenSourceDoc={openSourceDoc}
                                onPlayFromTimestamp={handlePlayFromTimestamp}
                                focusTs={focus?.ts ?? null}
                                focusN={focus?.n ?? 0}
                                isChat={isTextUpload}
                            />
                        </TabsContent>
                    )}

                    {/* Delivery */}
                    <TabsContent value="delivery" className="mt-5 space-y-6 flex-1 overflow-y-auto pb-4 pr-4">
                        <DeliveryPanel
                            transcript={session.transcript}
                            sessionDurationS={session.duration ?? undefined}
                            beSpeechMetrics={report.speech_metrics ?? undefined}
                            isChat={isTextUpload}
                        />
                    </TabsContent>
                </Tabs>
            </div>
            {/* Opened by a citation chip in the dimension panels. */}
            <KnowledgeDocDrawer
                doc={sourceDoc}
                onClose={() => setSourceDoc(null)}
                initialTab="extracted"
            />
        </div>
    );
}

// In AIQM recordings: speaker "agent" = trainee/CSR (CUSTOMER AGENT),
// speaker "user" = AI customer/caller (CUSTOMER).
// Map to persona/employee so transcriptToHighlights places:
//   employee (CUSTOMER AGENT) on the RIGHT
//   persona  (CUSTOMER)       on the LEFT
function aiqmTranscriptToTurns(entries: TranscriptEntry[]): TranscriptTurn[] {
    return entries.map((e) => {
        const m = Math.floor(e.timestamp_secs / 60);
        const s = Math.floor(e.timestamp_secs % 60);
        return {
            speaker: e.speaker === "agent" ? "employee" : "persona",
            text: e.content,
            // Chat/TXT sessions carry a wall-clock HH:MM label; audio calls fall
            // back to the elapsed m:ss computed from timestamp_secs.
            ts: e.ts_label ?? `${m}:${String(s).padStart(2, "0")}`,
        };
    });
}

// Uppercase tracking-wide section headers matching the "RUBRIC SCORE" style.
function TabSectionHeader({ title, subtitle }: { title: string; subtitle?: string }) {
    return (
        <div className="mb-3">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{title}</p>
            {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
        </div>
    );
}

// FE rubric labels come from RUBRIC_LABELS; a BE-provided criterion name is shown
// verbatim. For chat/TXT sessions the FE "Call Handling" label reads as "Chat
// Handling" (there is no call).
function rubricLabel(
    key: keyof RubricScores,
    criterion: RubricCriterion | undefined,
    isChat: boolean,
): string {
    if (criterion?.name) return criterion.name;
    if (isChat && key === "call_handling") return "Chat Handling";
    return RUBRIC_LABELS[key] ?? key;
}

// Normalize any chat time string ("9:04", "09:04:00", "17/03/2026 12:30:55") to a
// canonical "H:MM" so a feedback item's timestamp can be matched to a transcript
// entry's time regardless of the exact format the BE sent.
function chatClock(v?: string | null): string | null {
    if (!v) return null;
    const m = v.match(/(\d{1,2}):(\d{2})/);
    return m ? `${Number(m[1])}:${m[2]}` : null;
}

// Minutes-since-midnight for a chat clock, so a feedback timestamp can be matched
// to the CLOSEST transcript entry — transcript segments are grouped Q&A pairs, so
// a feedback time rarely equals a segment time exactly.
function chatMinutes(v?: string | null): number | null {
    const c = chatClock(v);
    if (!c) return null;
    const [h, m] = c.split(":").map(Number);
    return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : null;
}

function CompactRubricSection({
    scores,
    total,
    criteria,
    isChat = false,
}: {
    scores: RubricScores;
    total?: number;
    criteria?: RubricCriterion[];
    isChat?: boolean;
}) {
    const [drawerOpen, setDrawerOpen] = useState(false);
    const computedTotal =
        typeof total === "number"
            ? total
            : Object.values(scores).reduce((sum, v) => sum + v, 0);
    const band = rubricGradeBand(computedTotal);
    const rubricKeys = Object.keys(RUBRIC_LABELS) as (keyof RubricScores)[];

    return (
        <div className="mt-5 pt-4 border-t border-gray-100">
            <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Rubric Score
                </p>
                <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-gray-900 tabular-nums">
                        {computedTotal}<span className="text-gray-400 font-normal">/100</span>
                    </span>
                    {criteria && criteria.length > 0 ? (
                        <button
                            type="button"
                            onClick={() => setDrawerOpen(true)}
                            className="text-xs text-blue-600 underline hover:text-gray-900 transition-colors"
                        >
                            View Rubric
                        </button>
                    ) : (
                        <span className={cn("inline-flex items-center px-2 py-0.5 border rounded-full text-[10px] font-semibold", band.cls)}>
                            {band.label}
                        </span>
                    )}
                </div>
            </div>
            <div>
                {rubricKeys.map((key) => {
                    const criterion = criteria?.find(
                        (c) => c.name.toLowerCase() === (RUBRIC_LABELS[key] ?? key).toLowerCase(),
                    );
                    return (
                        <RubricBar
                            key={key}
                            label={rubricLabel(key, criterion, isChat)}
                            value={scores[key]}
                        />
                    );
                })}
            </div>

            {criteria && criteria.length > 0 && (
                <RubricSideDrawer
                    open={drawerOpen}
                    onClose={() => setDrawerOpen(false)}
                    criteria={criteria}
                />
            )}
        </div>
    );
}

function scoreChipColors(value: number): string {
    if (value >= 4) return "bg-emerald-50 text-emerald-700 border-emerald-200";
    if (value >= 3) return "bg-blue-50 text-blue-700 border-blue-200";
    if (value >= 2) return "bg-amber-50 text-amber-700 border-amber-200";
    return "bg-rose-50 text-rose-700 border-rose-200";
}

// Decide which dimension (and therefore which tab) a feedback item belongs to,
// mirroring AiqmDimensionPanel's filter precedence: rubric-label match → criterion
// name match → positive-category fallback (communication) → default (handling).
// Used to route a clicked timeline dot to the tab that actually shows the item.
function dimensionForItem(
    item: BeFeedbackItem,
    criteria?: RubricCriterion[],
): CoachingDimension {
    const titleLower = item.title.toLowerCase();
    const contentLower = item.content.toLowerCase();
    for (const dim of ["handling", "communication"] as CoachingDimension[]) {
        const rubricKeys = (Object.keys(RUBRIC_LABELS) as (keyof RubricScores)[]).filter(
            (key) => classifyRubricCriterion(RUBRIC_LABELS[key]) === dim,
        );
        for (const key of rubricKeys) {
            const label = (RUBRIC_LABELS[key] ?? key).toLowerCase();
            if (titleLower.includes(label) || contentLower.includes(label)) return dim;
        }
    }
    const criterion = criteria?.find((c) => {
        const name = c.name.toLowerCase();
        return titleLower.includes(name) || contentLower.includes(name);
    });
    if (criterion) return classifyRubricCriterion(criterion.name);
    if (
        item.category === "Brilliant" ||
        item.category === "Excellent" ||
        item.category === "Good"
    ) {
        return "communication";
    }
    return "handling";
}

function AiqmDimensionPanel({
    dim,
    scores,
    criteria,
    feedbackItems,
    knowledgeManifest,
    onOpenSourceDoc,
    onPlayFromTimestamp,
    focusTs = null,
    focusN = 0,
    isChat = false,
}: {
    dim: CoachingDimension;
    scores: RubricScores;
    criteria?: RubricCriterion[];
    feedbackItems?: BeFeedbackItem[];
    /** Resolves feedback-item citations to document names — see CoachingMomentCard. */
    knowledgeManifest?: KnowledgeManifest | null;
    onOpenSourceDoc?: (documentId: string) => void;
    onPlayFromTimestamp?: (ts: string) => void;
    // Auto-expand signal: when focusTs matches an item in this panel, that
    // card auto-expands; focusN bumps so re-clicks re-open it.
    focusTs?: string | null;
    focusN?: number;
    // Chat/TXT session: relabel the rubric chip + the feedback "Play from" action.
    isChat?: boolean;
}) {
    const rubricKeys = (Object.keys(RUBRIC_LABELS) as (keyof RubricScores)[]).filter(
        (key) => classifyRubricCriterion(RUBRIC_LABELS[key]) === dim,
    );

    const dimensionFeedback = (feedbackItems ?? []).filter((item) => {
        const titleLower = item.title.toLowerCase();
        const contentLower = item.content.toLowerCase();
        for (const key of rubricKeys) {
            const label = (RUBRIC_LABELS[key] ?? key).toLowerCase();
            if (titleLower.includes(label) || contentLower.includes(label)) return true;
        }
        const criterion = criteria?.find((c) => {
            const name = c.name.toLowerCase();
            return titleLower.includes(name) || contentLower.includes(name);
        });
        if (criterion) {
            return classifyRubricCriterion(criterion.name) === dim;
        }
        if (dim === "communication") {
            return item.category === "Brilliant" || item.category === "Excellent" || item.category === "Good";
        }
        return dim === "handling";
    });

    const allCategories = Array.from(new Set(dimensionFeedback.map((f) => f.category).filter(Boolean)));
    const [categoryFilter, setCategoryFilter] = useState<string>("all");
    // When a clicked timeline dot targets an item in THIS panel, clear any active
    // category filter so the targeted card is rendered and can auto-expand.
    useEffect(() => {
        if (
            focusTs &&
            dimensionFeedback.some((i) => (i.timestamp ?? "").trim() === focusTs)
        ) {
            setCategoryFilter("all");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [focusN]);

    // Early return must come AFTER all hooks (rules-of-hooks).
    if (rubricKeys.length === 0) {
        return (
            <p className="text-sm text-gray-400 italic py-4">
                No rubric criteria for this dimension.
            </p>
        );
    }

    const visibleFeedback = categoryFilter === "all"
        ? dimensionFeedback
        : dimensionFeedback.filter((f) => f.category === categoryFilter);

    const CHIP_INACTIVE: Record<string, string> = {
        Brilliant: "bg-purple-50 text-purple-700 border border-purple-100 hover:bg-purple-100",
        Excellent:  "bg-green-50 text-green-700 border border-green-100 hover:bg-green-100",
        Good:       "bg-emerald-50 text-emerald-700 border border-emerald-100 hover:bg-emerald-100",
        Inaccuracy: "bg-amber-50 text-amber-700 border border-amber-100 hover:bg-amber-100",
        Mistake:    "bg-rose-50 text-rose-700 border border-rose-100 hover:bg-rose-100",
    };
    const CHIP_ACTIVE: Record<string, string> = {
        Brilliant: "bg-purple-600 text-white border border-purple-600 shadow-sm",
        Excellent:  "bg-green-600 text-white border border-green-600 shadow-sm",
        Good:       "bg-emerald-600 text-white border border-emerald-600 shadow-sm",
        Inaccuracy: "bg-amber-500 text-white border border-amber-500 shadow-sm",
        Mistake:    "bg-rose-600 text-white border border-rose-600 shadow-sm",
    };
    const CHIP_COUNT_INACTIVE: Record<string, string> = {
        Brilliant: "bg-purple-100 text-purple-800",
        Excellent:  "bg-green-100 text-green-800",
        Good:       "bg-emerald-100 text-emerald-800",
        Inaccuracy: "bg-amber-100 text-amber-800",
        Mistake:    "bg-rose-100 text-rose-800",
    };

    return (
        <div className="space-y-6">
            {/* Rubric score chips */}
            {rubricKeys.length > 0 && (
                <div className="flex flex-wrap gap-2">
                    {rubricKeys.map((key) => {
                        const value = scores[key];
                        const label = rubricLabel(
                            key,
                            criteria?.find(
                                (c) => c.name.toLowerCase() === (RUBRIC_LABELS[key] ?? key).toLowerCase(),
                            ),
                            isChat,
                        );
                        return (
                            <span
                                key={key}
                                className={cn(
                                    "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium",
                                    scoreChipColors(value),
                                )}
                            >
                                <span>{label}</span>
                                <span className="font-bold tabular-nums">{value}/5</span>
                            </span>
                        );
                    })}
                </div>
            )}

            {/* Filter by Category */}
            <div>
                {allCategories.length > 0 && (
                    <div className="mb-4">
                    <p className="text-[11px] uppercase tracking-wider text-neutral-500 font-medium mb-2">
                        Filter by category
                    </p>
                    <div className="flex flex-wrap gap-2">
                        {/* All chip */}
                        <button
                            type="button"
                            onClick={() => setCategoryFilter("all")}
                            className={cn(
                                "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold transition-colors",
                                categoryFilter === "all"
                                    ? "bg-blue-600 text-white border border-blue-600 shadow-sm"
                                    : "bg-blue-50 text-blue-700 border border-blue-100 hover:bg-blue-100",
                            )}
                        >
                            All
                            <span className={cn(
                                "inline-flex items-center justify-center rounded-full text-[10px] font-semibold min-w-[16px] h-4 px-1",
                                categoryFilter === "all" ? "bg-white/20 text-white" : "bg-blue-100 text-blue-800",
                            )}>
                                {dimensionFeedback.length}
                            </span>
                        </button>
                        {allCategories.map((cat) => {
                            const count = dimensionFeedback.filter((f) => f.category === cat).length;
                            const isActive = categoryFilter === cat;
                            return (
                                <button
                                    key={cat}
                                    type="button"
                                    onClick={() => setCategoryFilter(cat)}
                                    className={cn(
                                        "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold transition-colors",
                                        isActive
                                            ? (CHIP_ACTIVE[cat] ?? "bg-gray-600 text-white border border-gray-600 shadow-sm")
                                            : (CHIP_INACTIVE[cat] ?? "bg-gray-50 text-gray-700 border border-gray-100 hover:bg-gray-100"),
                                    )}
                                >
                                    {cat}
                                    <span className={cn(
                                        "inline-flex items-center justify-center rounded-full text-[10px] font-semibold min-w-[16px] h-4 px-1",
                                        isActive ? "bg-white/20 text-white" : (CHIP_COUNT_INACTIVE[cat] ?? "bg-gray-100 text-gray-800"),
                                    )}>
                                        {count}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                    </div>
                )}

                {visibleFeedback.length > 0 ? (
                    <div className="divide-y divide-gray-100">
                        {visibleFeedback.map((item, idx) => (
                            <CoachingMomentCard
                                key={idx}
                                item={item}
                                rubricCriterion={criteria?.find(
                                    (c) => {
                                        const name = c.name.toLowerCase();
                                        return item.title.toLowerCase().includes(name) || item.content.toLowerCase().includes(name);
                                    }
                                )}
                                knowledgeManifest={knowledgeManifest}
                                onOpenSourceDoc={onOpenSourceDoc}
                                onPlayFromTimestamp={onPlayFromTimestamp}
                                quoteLabel="Quoted from their answer"
                                isChat={isChat}
                                // Open the top moment by default so the Issue Resolution
                                // and Communication tabs land on something readable rather
                                // than all-collapsed rows (matches the roleplay results
                                // panel). Delivery uses a separate panel and is unaffected.
                                defaultExpanded={idx === 0}
                                expandKey={
                                    focusTs && (item.timestamp ?? "").trim() === focusTs
                                        ? focusN
                                        : null
                                }
                            />
                        ))}
                    </div>
                ) : (
                    <div className="flex flex-col items-center justify-center py-10 px-4 text-center rounded-lg border border-gray-100">
                        <p className="text-xs text-gray-400">No feedback items for this dimension yet.</p>
                    </div>
                )}
            </div>
        </div>
    );
}

const FILLER_REGEX = /\b(um+|uh+|err+|like|you know|sort of|kind of|i mean|basically|literally|actually)\b/gi;
const FILLER_WORDS = ["um", "uh", "err", "like", "you know", "sort of", "kind of", "i mean", "basically", "literally", "actually"];

function buildAiqmSpeechMetrics(transcript: TranscriptEntry[]): {
    metrics: SpeechMetrics;
    fillerBreakdown: Record<string, number>;
} {
    if (!transcript.length) {
        return {
            metrics: { wordsPerMinute: 0, fillerPerMinute: 0, fillerCount: 0, talkListenRatio: "50 : 50", longestMonologueSec: 0, totalWords: 0, isMocked: true },
            fillerBreakdown: {},
        };
    }

    const sorted = [...transcript].sort((a, b) => a.timestamp_secs - b.timestamp_secs);
    // "agent" = CS trainee (CUSTOMER AGENT), "user" = AI customer (CUSTOMER)
    const agentTurns = sorted.filter((e) => e.speaker === "agent");
    const customerTurns = sorted.filter((e) => e.speaker === "user");

    const countWords = (text: string) => text.trim().split(/\s+/).filter(Boolean).length;
    const agentText = agentTurns.map((e) => e.content).join(" ");
    const agentWords = countWords(agentText);
    const customerWords = customerTurns.reduce((n, e) => n + countWords(e.content), 0);
    const totalWords = agentWords + customerWords;

    const durationSecs = sorted[sorted.length - 1].timestamp_secs - sorted[0].timestamp_secs;
    const durationMin = Math.max(0.5, durationSecs / 60);
    const wordsPerMinute = Math.round(agentWords / durationMin);

    const fillerMatches = agentText.match(FILLER_REGEX) ?? [];
    const fillerCount = fillerMatches.length;
    const fillerPerMinute = Math.round((fillerCount / durationMin) * 10) / 10;

    const fillerBreakdown: Record<string, number> = {};
    for (const match of fillerMatches) {
        const word = match.toLowerCase();
        const canonical = FILLER_WORDS.find((w) => word.includes(w)) ?? word;
        fillerBreakdown[canonical] = (fillerBreakdown[canonical] ?? 0) + 1;
    }

    const agentPct = totalWords > 0 ? Math.round((agentWords / totalWords) * 100) : 50;
    const talkListenRatio = `${agentPct} : ${100 - agentPct}`;

    const wps = wordsPerMinute > 0 ? wordsPerMinute / 60 : 2.5;
    let longestMonologueSec = 0;
    for (const turn of agentTurns) {
        const dur = countWords(turn.content) / wps;
        if (dur > longestMonologueSec) longestMonologueSec = dur;
    }

    return {
        metrics: {
            wordsPerMinute: wordsPerMinute || 0,
            fillerPerMinute: fillerPerMinute || 0,
            fillerCount,
            talkListenRatio,
            longestMonologueSec: Math.round(longestMonologueSec),
            totalWords,
            isMocked: agentWords < 20,
        },
        fillerBreakdown,
    };
}

function DeliveryPanel({
    transcript,
    sessionDurationS,
    beSpeechMetrics,
    isChat = false,
}: {
    transcript: TranscriptEntry[];
    sessionDurationS?: number;
    beSpeechMetrics?: BeSpeechMetrics;
    // Text/chat (*.txt) sessions relabel "Talk" wording to "Chat".
    isChat?: boolean;
}) {
    const clientComputed = buildAiqmSpeechMetrics(transcript);
    const metrics: SpeechMetrics = beSpeechMetrics
        ? mapBeSpeechMetrics(beSpeechMetrics)
        : clientComputed.metrics;
    const fillerBreakdown: Record<string, number> = beSpeechMetrics
        ? beSpeechMetrics.filler_breakdown
        : clientComputed.fillerBreakdown;

    if (!transcript.length) {
        return (
            <p className="text-sm text-gray-500 italic py-4 text-center">
                No transcript data available.
            </p>
        );
    }

    const sorted = [...transcript].sort((a, b) => a.timestamp_secs - b.timestamp_secs);
    // Prefer the BE-supplied duration (accurate from STT); fall back to transcript max timestamp.
    const totalSecs = typeof sessionDurationS === "number" && sessionDurationS > 0
        ? sessionDurationS
        : sorted[sorted.length - 1].timestamp_secs;
    const durMin = Math.floor(totalSecs / 60);
    const durSec = Math.floor(totalSecs % 60);
    const durationFormatted = `${durMin}:${String(durSec).padStart(2, "0")}`;

    // Talk distribution: prefer BE-computed percentages (more accurate); fall back to transcript word counts.
    const countWords = (text: string) => text.trim().split(/\s+/).filter(Boolean).length;
    const agentWords = beSpeechMetrics?.total_employee_words
        ?? sorted.filter((e) => e.speaker === "agent").reduce((n, e) => n + countWords(e.content), 0);
    const customerWords = sorted.filter((e) => e.speaker === "user").reduce((n, e) => n + countWords(e.content), 0);
    const agentPct = beSpeechMetrics
        ? beSpeechMetrics.talk_listen_ratio.employee_pct
        : (agentWords + customerWords > 0 ? Math.round((agentWords / (agentWords + customerWords)) * 100) : 50);
    const customerPct = 100 - agentPct;

    const gaps = sorted.slice(1).map((e, i) => e.timestamp_secs - sorted[i].timestamp_secs);
    const turnsExceeding3s = gaps.filter((g) => g > 3).length;

    let pattern: LatencyPattern = "Stable";
    if (gaps.length >= 4) {
        const mean = gaps.reduce((a, b) => a + b, 0) / gaps.length;
        const variance = gaps.reduce((s, g) => s + (g - mean) ** 2, 0) / gaps.length;
        const stdDev = Math.sqrt(variance);
        const mid = Math.floor(gaps.length / 2);
        const firstAvg = gaps.slice(0, mid).reduce((a, b) => a + b, 0) / mid;
        const secondAvg = gaps.slice(mid).reduce((a, b) => a + b, 0) / (gaps.length - mid);
        if (stdDev > 4) pattern = "Erratic";
        else if (secondAvg > firstAvg * 1.4) pattern = "Degrading";
    }

    return (
        <div className="space-y-6">
            {/* Talk distribution — shown first */}
            <div>
                <TabSectionHeader title={isChat ? "Chat Distribution" : "Talk Distribution"} />
                <div className="flex justify-between text-xs text-gray-500 mb-1.5">
                    <span>Customer Agent {agentPct}%</span>
                    <span>Customer {customerPct}%</span>
                </div>
                <div className="flex h-2 rounded-full overflow-hidden mb-1">
                    <div className="bg-blue-500 transition-all" style={{ width: `${agentPct}%` }} />
                    <div className="bg-emerald-500 transition-all" style={{ width: `${customerPct}%` }} />
                </div>
                <div className="flex justify-between text-xs text-gray-400">
                    <span>{agentWords} words</span>
                    <span>{customerWords} words</span>
                </div>
                {/* 4 stats in one row */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
                    <TimingStat label="Duration" value={durationFormatted} />
                    <TimingStat label="Total turns" value={String(sorted.length)} />
                    <TimingStat label="Slow turns" value={String(turnsExceeding3s)} />
                    <TimingStat label="Response pattern" value={pattern} />
                </div>
            </div>

            {/* Speech metrics */}
            <SpeechCoachingStrip metrics={metrics} isChat={isChat} />

            {/* Filler words breakdown */}
            <div>
                <TabSectionHeader title="Filler Words Breakdown" />
                {Object.keys(fillerBreakdown).length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                        {Object.entries(fillerBreakdown)
                            .sort(([, a], [, b]) => b - a)
                            .map(([word, count]) => (
                                <span
                                    key={word}
                                    className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1 text-xs text-gray-700 font-medium"
                                >
                                    {word.charAt(0).toUpperCase() + word.slice(1)}
                                    <span className="text-gray-400">× {count}</span>
                                </span>
                            ))}
                    </div>
                ) : (
                    <p className="text-xs text-gray-400">No filler words detected.</p>
                )}
            </div>
        </div>
    );
}

function TimingStat({ label, value }: { label: string; value: string }) {
    return (
        <div className="rounded-lg bg-gray-50 p-3">
            <div className="text-[10px] text-gray-400 uppercase tracking-wide mb-1">{label}</div>
            <div className="text-sm font-semibold text-gray-900 tabular-nums">{value}</div>
        </div>
    );
}

function ActionItemsList({ items }: { items: ActionItem[] }) {
    const humanizeCategory = (category: string) =>
        category
            .split("_")
            .filter(Boolean)
            .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
            .join(" ");

    const sorted = [...items].sort((a, b) => {
        const order: Record<string, number> = { high: 0, medium: 1, low: 2 };
        const ap = order[a.priority] ?? 99;
        const bp = order[b.priority] ?? 99;
        return ap - bp;
    });

    return (
        <div className="space-y-2">
            {sorted.map((item, i) => {
                const colors = priorityColor(item.priority);
                return (
                    <div key={i} className="border border-gray-100 rounded-lg p-3">
                        <div className="flex items-start gap-2.5">
                            <ArrowRightIcon className={cn("w-4 h-4 flex-shrink-0 mt-0.5", colors.text)} />
                            <div className="flex-1 min-w-0">
                                <p className="text-sm text-gray-800">
                                    <span className={cn("font-semibold capitalize mr-1", colors.text)}>
                                        {item.priority}:
                                    </span>
                                    {item.description}
                                </p>
                                {item.technical_suggestion && (
                                    <p className="text-xs text-gray-500 mt-1.5">{item.technical_suggestion}</p>
                                )}
                                {(item.location || item.category) && (
                                    <div className="flex gap-3 mt-1.5 text-xs text-gray-400">
                                        {item.location && <span>@ {item.location}</span>}
                                        {item.category && <span>{humanizeCategory(item.category)}</span>}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

export function RubricSnapshotModal({
    open,
    onClose,
    criteria,
}: {
    open: boolean;
    onClose: () => void;
    criteria: RubricCriterion[];
}) {
    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
                <DialogHeader>
                    <DialogTitle>Rubric used for this upload</DialogTitle>
                    <p className="text-xs text-gray-500 mt-1">
                        This is the exact rubric that was applied when this audio was scored. It is locked to this session and cannot be edited.
                    </p>
                </DialogHeader>
                <div className="flex-1 overflow-y-auto py-2 space-y-3">
                    {criteria.map((criterion) => (
                        <div key={criterion.id} className="rounded-lg border border-gray-200 bg-white p-4">
                            <div className="flex items-center justify-between gap-3 mb-2">
                                <h3 className="text-sm font-semibold text-gray-900">{criterion.name}</h3>
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100">
                                    {criterion.weight}%
                                </span>
                            </div>
                            {criterion.description && (
                                <p className="text-xs text-gray-500 mb-3">{criterion.description}</p>
                            )}
                        </div>
                    ))}
                </div>
            </DialogContent>
        </Dialog>
    );
}

function RubricSideDrawer({
    open,
    onClose,
    criteria,
}: {
    open: boolean;
    onClose: () => void;
    criteria: RubricCriterion[];
}) {
    if (!open) return null;
    return (
        <div
            className="fixed inset-0 bg-black/40 z-50 flex justify-end"
            onClick={onClose}
        >
            <div
                className="bg-white w-full max-w-2xl h-full flex flex-col shadow-xl"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4 flex-shrink-0">
                    <div>
                        <h2 className="text-base font-semibold text-gray-900">Scoring Rubric</h2>
                        <p className="text-xs text-gray-500 mt-0.5">
                            Criteria applied when this audio was scored. Locked to this session.
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="w-8 h-8 flex items-center justify-center rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                        aria-label="Close"
                    >
                        <XIcon className="w-4 h-4" />
                    </button>
                </div>
                <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
                    {criteria.map((criterion) => (
                        <div key={criterion.id} className="rounded-lg border border-gray-200 bg-white p-4">
                            <div className="flex items-center justify-between gap-3 mb-2">
                                <h3 className="text-sm font-semibold text-gray-900">{criterion.name}</h3>
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100">
                                    {criterion.weight}%
                                </span>
                            </div>
                            {criterion.description && (
                                <p className="text-xs text-gray-500 leading-relaxed mb-3">{criterion.description}</p>
                            )}
                            {criterion.correct_answer && (
                                <div className="mt-3 pt-3 border-t border-gray-100">
                                    <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">
                                        What a good answer looks like
                                    </p>
                                    <ul className="space-y-1.5">
                                        {criterion.correct_answer.split("\n").filter(Boolean).map((line, li) => (
                                            <li key={li} className="flex items-start gap-2 text-sm text-gray-700">
                                                <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-blue-400 flex-shrink-0" />
                                                {line}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            {criterion.scoring_guide && (
                                <div className="space-y-2 mt-3 pt-3 border-t border-gray-100">
                                    {criterion.scoring_guide.split("\n").filter(Boolean).map((line, i) => {
                                        const isHigh = line.startsWith("High");
                                        const isMid = line.startsWith("Mid");
                                        const text = line.replace(/^(High|Mid|Low) score\s*[:—]\s*/, "");
                                        const label = isHigh ? "High" : isMid ? "Mid" : "Low";
                                        const cls = isHigh
                                            ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                                            : isMid
                                            ? "bg-amber-50 text-amber-700 border-amber-100"
                                            : "bg-red-50 text-red-700 border-red-100";
                                        return (
                                            <div key={i} className="grid grid-cols-[3rem_1fr] gap-2 text-xs">
                                                <span className={cn("inline-flex items-center justify-center px-1.5 py-0.5 rounded border text-[10px] font-semibold", cls)}>
                                                    {label}
                                                </span>
                                                <span className="text-gray-600 leading-5">{text}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

// Empty state mirroring the training-results page (src/pages/training-results):
// a centered card with "Results not available" and a Retry button. Shown when
// the session finished loading but no session data came back.
function AiqmEmptyState() {
    return (
        <div className="flex h-full items-center justify-center p-6">
            <Card className="max-w-md">
                <CardContent className="space-y-4 p-6 text-center">
                    <p className="text-sm text-gray-600">Results not available.</p>
                    <Button variant="outline" onClick={() => window.location.reload()}>
                        Retry
                    </Button>
                </CardContent>
            </Card>
        </div>
    );
}

function PendingState({ session }: { session: CallSession }) {
    return (
        <Card>
            <CardContent className="p-12 text-center">
                {session.status === "analyzing" ? (
                    <>
                        <Loader2Icon className="w-10 h-10 text-amber-500 mx-auto mb-3 animate-spin" />
                        <h3 className="text-sm font-medium text-gray-900 mb-1">Analysis in progress</h3>
                        <p className="text-xs text-gray-500 max-w-sm mx-auto">
                            AI is evaluating the call quality. This usually takes less than a minute.
                        </p>
                    </>
                ) : session.status === "failed" ? (
                    <>
                        <XCircleIcon className="w-10 h-10 text-rose-400 mx-auto mb-3" />
                        <h3 className="text-sm font-medium text-gray-900 mb-1">Analysis failed</h3>
                        <p className="text-xs text-gray-500 max-w-sm mx-auto">
                            Something went wrong analyzing this call. The error has been logged.
                        </p>
                    </>
                ) : (
                    <>
                        <ClockIcon className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                        <h3 className="text-sm font-medium text-gray-900 mb-1">Waiting for results</h3>
                        <p className="text-xs text-gray-500 max-w-sm mx-auto">
                            This call session is {session.status}. Results will appear here once analysis completes.
                        </p>
                    </>
                )}
            </CardContent>
        </Card>
    );
}

function SkeletonDetail() {
    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
                <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gray-100 animate-pulse" />
                    <div className="flex-1">
                        <div className="h-5 w-48 bg-gray-100 rounded animate-pulse mb-2" />
                        <div className="h-3 w-40 bg-gray-100 rounded animate-pulse" />
                    </div>
                </div>
                <div className="h-48 bg-gray-50 rounded animate-pulse" />
            </div>
            <div className="space-y-4">
                <div className="h-8 w-full bg-gray-50 rounded animate-pulse" />
                <div className="h-48 bg-gray-50 rounded animate-pulse" />
            </div>
        </div>
    );
}
